import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGet, apiPost, apiDelete } from '../../utils/apiFetch';
import apiPath from '../../utils/apiPath';
import ODataTable from '../../components/reusable/ODataTable';
import OPagination from '../../components/reusable/OPagination';
import OButton from '../../components/reusable/OButton';
import { AiOutlineEye, AiOutlinePlus, AiOutlineDelete } from 'react-icons/ai';
import helpers from '../../utils/helpers';

const ResumeCatalog = () => {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageCount: 0, totalItems: 0, limit: 10 });
  const [keywordInput, setKeywordInput] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    designation: '',
    resume: null
  });

  const fetchCatalog = async (page = 1, keyword = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('pageSize', pagination.limit);
      if (keyword) params.set('keyword', keyword);
      
      const res = await apiGet(`${apiPath.resumeCatalog}?${params.toString()}`);
      const results = res?.data?.results || {};
      const items = results?.docs || [];
      
      setList(items);
      setPagination({
        page: results.page || page,
        pageCount: results.totalPages || 0,
        totalItems: results.totalDocs || 0,
        limit: results.limit || pagination.limit
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalog(pagination.page, keywordInput);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchCatalog(1, keywordInput);
    }, 300);
    return () => clearTimeout(handler);
  }, [keywordInput]);

  const handlePageChange = (newPage) => {
    fetchCatalog(newPage, keywordInput);
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'resume') {
      setFormData(prev => ({ ...prev, resume: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.resume) return;

    try {
      const payload = new FormData();
      payload.append('name', formData.name);
      if (formData.email) payload.append('email', formData.email);
      if (formData.phoneNumber) payload.append('phoneNumber', formData.phoneNumber);
      if (formData.designation) payload.append('designation', formData.designation);
      payload.append('resume', formData.resume);

      const res = await apiPost(apiPath.resumeCatalog, payload);
      if (res.data.success) {
        setIsModalOpen(false);
        setFormData({ name: '', email: '', phoneNumber: '', designation: '', resume: null });
        fetchCatalog(1, keywordInput);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const deleteCandidate = async (id) => {
    try {
      setLoading(true);
      const res = await apiDelete(`${apiPath.resumeCatalog}/${id}`);
      if (res?.data?.success) {
        fetchCatalog(pagination.page, keywordInput);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: 'Name', key: 'name', sortable: false, render: (row) => row.name },
    { title:  'Email', key: 'email', sortable: false, render: (row) => row.email || '-' },
    { title:  'Phone', key: 'phoneNumber', sortable: false, render: (row) => row.phoneNumber || '-' },
    { title:  'Designation', key: 'designation', sortable: false, render: (row) => row.designation || '-' },
    { title:  'Added At', key: 'createdAt', sortable: false, render: (row) => new Date(row.createdAt).toLocaleString() },
    { 
      title: t('O_ACTION') || 'Action', 
      key: 'action',
      align: 'center',
      render: (row) => (
        <div className="flex items-center gap-3 justify-center w-full">
          <a 
            href={row.resume} 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:text-blue-700 w-[18px] h-[18px] flex items-center justify-center transition-colors text-gray-500"
            title={t("O_VIEW") || 'View'}
          >
            <AiOutlineEye size={18} /> 
          </a>
          <button
            onClick={() =>
                helpers.alertFunction(
                    t('O_ARE_YOU_SURE_DELETE') || 'Are you sure you want to delete this candidate?',
                    row._id,
                    deleteCandidate,
                    true
                )
            }
            className="hover:text-red-600 w-[18px] h-[18px] flex items-center justify-center transition-colors text-gray-500"
            title={t("O_DELETE") || 'Delete'}
          >
            <AiOutlineDelete size={18} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div>
      <div className="bg-white">
        <div className="px-3 py-4">
          <div className="bg-white border border-[#E9EDF9]">
            <div className="border-b border-b-[#E3E3E3] flex flex-wrap items-end gap-2 px-4 py-3">
              <div className="w-full md:w-60">
                <label className="block mb-2 text-sm font-medium text-gray-900">
                  {t("O_SEARCH")}
                </label>
                <input
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  placeholder={t("SEARCH_BY_KEYWORD")}
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                />
              </div>
              <div className="ml-auto">
                <OButton
                  label={t("Add Candidate") || "Add Candidate"}
                  onClick={() => setIsModalOpen(true)}
                />
              </div>
            </div>

            <ODataTable
              data={list}
              columns={columns}
              loading={loading}
              pagination={pagination}
              onPageChange={handlePageChange}
            />
            {pagination.totalItems > 0 && (
              <OPagination
                pagination={pagination}
                onPageChange={handlePageChange}
                page={pagination.page}
              />
            )}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-2xl">
            <h3 className="text-lg font-bold mb-4">{t('Add Candidate') || 'Add Candidate'}</h3>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('Name')} <span className="text-red-500">*</span></label>
                  <input 
                    type="text" 
                    name="name" 
                    value={formData.name} 
                    onChange={handleInputChange} 
                    required 
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('Email')}</label>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleInputChange} 
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('Phone')}</label>
                  <input 
                    type="text" 
                    name="phoneNumber" 
                    value={formData.phoneNumber} 
                    onChange={handleInputChange} 
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('Designation')}</label>
                  <input 
                    type="text" 
                    name="designation" 
                    value={formData.designation} 
                    onChange={handleInputChange} 
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('Resume (PDF)')} <span className="text-red-500">*</span></label>
                  <input 
                    type="file" 
                    name="resume" 
                    onChange={handleInputChange} 
                    required 
                    accept=".pdf"
                    className="w-full pt-1"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-[#8FC150] text-white rounded">Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumeCatalog;
